import { Fragment, useEffect, useRef } from 'react';
import { Box, Flex, SxStyleProp } from 'theme-ui';
import Icon, { IconTypes } from 'shared/Icon';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslator } from '@picsart/rc/services/localization';
import { getActiveTool, setActiveTool } from 'modules/activeTool';
import { getCurrentTextId } from 'modules/currentTextId';
import { deleteEffectsFromScene, duplicateEffects, getActiveEffects } from 'modules/effects';
import { getReplaceState, setReplacing } from 'modules/replaceSceneContent';
import { getCurrentSceneId } from 'modules/scenes';
import { isEmpty } from 'ramda';
import { ActiveTool, ToolName } from 'types/AppState';

import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import { addAlpha, getCSSVariableValue } from 'utils/theme';
import { saveRevision } from 'modules/revisions';

export const textModificationTools = [
  ActiveTool.Font,
  ActiveTool.Color,
  ActiveTool.Shadow,
  ActiveTool.Outline
];

const floatingBoxStyles: SxStyleProp = {
  display: 'flex',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  px: '11px',
  height: '52px',
  ml: '8px',
  ':first-of-type': {
    ml: '0'
  }
};

const itemWrapperStyle: SxStyleProp = {
  height: '100%',
  ml: '2px',
  ':first-of-type': {
    ml: '0'
  }
};

const itemInnerWrapperStyle: SxStyleProp = {
  py: '9px'
};

const itemStyles: SxStyleProp = {
  width: '34px',
  height: '34px',
  borderRadius: '8px',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  ml: '2px',
  ':hover': {
    backgroundColor: (theme: any) => `${addAlpha(getCSSVariableValue(theme.colors.grey2), 0.6)}`
  },
  ':first-of-type': {
    ml: 0
  }
};

const itemActiveStyles: SxStyleProp = {
  ...itemStyles,
  backgroundColor: '#F6E8F1',
  ':hover': null
};

type ItemProps = {
  icon: string;
  active: boolean;
  onClick?: () => void;
  text: string;
  dataTest?: string;
};

const Item = ({ icon, active, onClick, text, dataTest }: ItemProps) => {
  return (
    <Box onClick={onClick} sx={itemWrapperStyle} data-test={dataTest}>
      <Tooltip content={text} placement={placementsList.bottom}>
        <Box sx={itemInnerWrapperStyle}>
          <Flex sx={active ? itemActiveStyles : itemStyles}>
            <Icon
              sx={{ display: 'block', width: '24px' }}
              name={icon}
              type={active ? IconTypes.Primary : IconTypes.FloatingSettings}
            />
          </Flex>
        </Box>
      </Tooltip>
    </Box>
  );
};

export const FloatingSettings = () => {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const activeTool = useSelector(getActiveTool);
  const currentTextIds = useSelector(getCurrentTextId);
  const effects = useSelector(getActiveEffects);
  const { isReplaceAvailable } = useSelector(getReplaceState);
  const currentSceneId = useSelector(getCurrentSceneId);
  const prevActiveTool = useRef<ActiveTool>(ActiveTool.Text);

  useEffect(() => {
    if (!textModificationTools.includes(activeTool)) {
      prevActiveTool.current = activeTool;
    }
  }, [activeTool]);

  useEffect(() => {
    if (!currentTextIds.length) {
      dispatch(setActiveTool(prevActiveTool.current));
    }
  }, [currentTextIds]);

  return (
    <Flex
      data-test="settings"
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        py: '4px',
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        top: 0,
        zIndex: 'floatingSettings'
      }}
    >
      {!isEmpty(effects) && currentTextIds.length && (
        <Fragment>
          <Box sx={floatingBoxStyles}>
            <Item
              icon="font-editor"
              active={activeTool === ActiveTool.Font}
              onClick={() => dispatch(setActiveTool(ActiveTool.Font))}
              text={translator('Font')}
              dataTest="settings_text-font-button"
            />

            <Item
              icon="color-editor"
              active={activeTool === ActiveTool.Color}
              onClick={() => dispatch(setActiveTool(ActiveTool.Color))}
              text={translator('Color')}
              dataTest="settings_text-color-button"
            />

            <Item
              icon="stroke-editor"
              active={activeTool === ActiveTool.Outline}
              onClick={() => dispatch(setActiveTool(ActiveTool.Outline))}
              text={translator('Outline')}
              dataTest="settings_text-outline-button"
            />

            <Item
              icon="shadow-editor"
              active={activeTool === ActiveTool.Shadow}
              onClick={() => dispatch(setActiveTool(ActiveTool.Shadow))}
              text={translator('Shadow')}
              dataTest="settings_text-shadow-button"
            />
          </Box>

          <Box sx={floatingBoxStyles}>
            <Item
              icon="duplicate-editor"
              active={false}
              text={translator('Duplicate')}
              onClick={() => {
                dispatch(duplicateEffects(currentTextIds, currentSceneId));
              }}
              dataTest="settings_duplicate-button"
            />
            <Item
              icon="delete-editor"
              active={false}
              onClick={() => {
                dispatch(deleteEffectsFromScene(currentTextIds, currentSceneId));
                dispatch(saveRevision(ToolName.Text));
              }}
              text={translator('Remove')}
              dataTest="settings_remove-button"
            />
          </Box>
        </Fragment>
      )}
      {isReplaceAvailable && (
        <Box sx={floatingBoxStyles}>
          <Item
            icon="fit-editor"
            active={activeTool === ActiveTool.PhotoSidebar}
            text={translator('Replace')}
            onClick={() => {
              dispatch(setReplacing());
              dispatch(setActiveTool(ActiveTool.PhotoSidebar));
            }}
            dataTest="settings_replace-button-photo"
          />
          <Item
            icon="video-editor"
            active={activeTool === ActiveTool.StockVideos}
            text={translator('Replace video')}
            onClick={() => {
              dispatch(setReplacing());
              dispatch(setActiveTool(ActiveTool.StockVideos));
            }}
            dataTest="settings_replace-button-video"
          />
        </Box>
      )}
    </Flex>
  );
};
